import React, { useState, useEffect, useCallback } from "react";
import { apiService, CreateCodeRequest, CodeItem } from "../../services";
import { authService } from "../../services/auth";
import CodeFilters from "./CodeFilters";
import CodeHeader from "./CodeHeader";
import CodePagination from "./CodePagination";
import CreateCodeModal from "./CreateCodeModal";
import DeleteCodesModal from "./DeleteCodesModal";
import ConfirmDeleteCodeModal from "./ConfirmDeleteCodeModal";
import ExportCodeModal from "./ExportCodeModal";

// Local interface for display purposes
interface Code {
  id: string;
  batch: string;
  code: string;
  status: "unused" | "used";
  campaign: string;
  usedDate?: string;
  createdAt: string;
}

interface CodeManagementProps {
  onPageChange: (page: string) => void;
}

const CodeManagement: React.FC<CodeManagementProps> = ({ onPageChange }) => {
  // Check user role
  const isAdmin = authService.isAdmin();

  // State management
  const [codes, setCodes] = useState<Code[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");

  // API pagination state
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // API statistics state
  const [totalCode, setTotalCode] = useState(0);
  const [totalCodeUsed, setTotalCodeUsed] = useState(0);
  const [totalCodeNotUsed, setTotalCodeNotUsed] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(isAdmin ? "all" : "used"); // User mặc định chỉ xem mã đã sử dụng
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [batchFilter, setBatchFilter] = useState("all");
  const [usedDateStart, setUsedDateStart] = useState("");
  const [usedDateEnd, setUsedDateEnd] = useState("");
  const [createdDateStart, setCreatedDateStart] = useState("");
  const [createdDateEnd, setCreatedDateEnd] = useState("");

  // Campaigns for filter dropdown
  const [campaigns, setCampaigns] = useState<
    Array<{ id: string; name: string }>
  >([]);

  // Batches for filter dropdown
  const [batches, setBatches] = useState<
    Array<{ id: string; name: string }>
  >([]);

  // Create code modal
  const [createModal, setCreateModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false,
  });

  // Delete codes modal
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Delete single code modal
  const [deleteCodeModal, setDeleteCodeModal] = useState<{
    isOpen: boolean;
    codeId: string;
    code: string;
  }>({
    isOpen: false,
    codeId: "",
    code: "",
  });
  const [isDeletingCode, setIsDeletingCode] = useState(false);

  // Export code modal
  const [exportModal, setExportModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false,
  });
  const [isExporting, setIsExporting] = useState(false);

  // Toast notification
  const showNotification = (
    message: string,
    type: "success" | "error" = "success"
  ) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Helper function to convert API data to display format
  const convertApiDataToDisplay = useCallback((apiData: CodeItem[]): Code[] => {
    return apiData.map((item) => {
      // Find campaign name by ID
      const campaign = campaigns.find((c) => c.id === item.campaign_id);
      const campaignName = campaign ? campaign.name : item.campaign_id || "";

      return {
        id: item._id,
        batch: item.batch_name || item.batch_id, // Use batch_name if available, fallback to batch_id
        code: item.code,
        status: item.is_used === 1 ? "used" : "unused",
        campaign: campaignName,
        usedDate: item.date_used || undefined,
        createdAt: item.createdAt,
      };
    });
  }, [campaigns]);

  // Raw API data state
  const [rawCodes, setRawCodes] = useState<CodeItem[]>([]);

  // Fetch data from API
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await apiService.getCodes(
        currentPage,
        rowsPerPage,
        campaignFilter !== "all" ? campaignFilter : undefined, // campaignId
        batchFilter !== "all" ? batchFilter : undefined, // batchId
        statusFilter !== "all"
          ? statusFilter === "used"
            ? "1"
            : "0"
          : undefined, // isUsed
        usedDateStart || undefined, // startDateUsed
        usedDateEnd || undefined, // endDateUsed
        createdDateStart || undefined, // startCreatedAt
        createdDateEnd || undefined, // endCreatedAt
        searchTerm || undefined // search
      );

      if (response.success) {
        // If filtering by campaign and no data returned, show empty result
        if (
          campaignFilter !== "all" &&
          (!response.data || response.data.length === 0)
        ) {
          setRawCodes([]);
          setTotalItems(0);
          setTotalPages(0);
          setTotalCode(0);
          setTotalCodeUsed(0);
          setTotalCodeNotUsed(0);
        } else {
          setRawCodes(response.data);
          setTotalItems(response.pagination.totalItems);
          setTotalPages(response.pagination.totalPages);
          setTotalCode(response.total_code || 0);
          setTotalCodeUsed(response.total_code_used || 0);
          setTotalCodeNotUsed(response.total_code_not_used || 0);
        }
      } else {
        throw new Error("Failed to fetch codes");
      }
    } catch (error: any) {
      console.error("Error fetching data:", error);
      showNotification("Lỗi khi tải dữ liệu", "error");
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    statusFilter,
    campaignFilter,
    batchFilter,
    usedDateStart,
    usedDateEnd,
    createdDateStart,
    createdDateEnd,
    searchTerm,
  ]);

  // Convert raw data to display format when campaigns or rawCodes change
  useEffect(() => {
    if (rawCodes.length > 0 && campaigns.length > 0) {
      const displayCodes = convertApiDataToDisplay(rawCodes);
      setCodes(displayCodes);
    } else if (rawCodes.length > 0) {
      // If campaigns not loaded yet, show raw data
      const displayCodes = rawCodes.map((item) => ({
        id: item._id,
        batch: item.batch_name || item.batch_id,
        code: item.code,
        status: (item.is_used === 1 ? "used" : "unused") as "unused" | "used",
        campaign: item.campaign_id || "",
        usedDate: item.date_used || undefined,
        createdAt: item.createdAt,
      }));
      setCodes(displayCodes);
    } else {
      // No data, clear the codes array
      setCodes([]);
    }
  }, [rawCodes, campaigns, convertApiDataToDisplay]);

  // Reset pagination when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Fetch data when component mounts or filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Since we're using server-side filtering and pagination, we don't need client-side filtering
  const paginatedCodes = codes;

  // Handle export codes - open modal
  const handleExportCodes = () => {
    setExportModal({ isOpen: true });
  };

  // Handle export codes with batch
  const handleExportCodesWithParams = async (batchId: string) => {
    try {
      setIsExporting(true);
      const blob = await apiService.exportCodesByBatchAndDate(batchId, "");

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `codes_batch_${batchId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportModal({ isOpen: false });
      showNotification(
        "Đã xuất file mã code thành công! File đã được tải về máy tính của bạn."
      );
    } catch (error: any) {
      console.error("Export codes error:", error);

      // Handle specific error messages
      let errorMessage =
        "Không thể xuất file mã code. Vui lòng kiểm tra kết nối mạng và thử lại.";

      if (error?.message === "No unused codes found") {
        errorMessage =
          "Không tìm thấy mã code chưa sử dụng cho batch đã chọn. Vui lòng chọn batch khác hoặc kiểm tra lại dữ liệu.";
      } else if (error?.message?.includes("404")) {
        errorMessage = "Không tìm thấy batch hoặc mã code.";
      } else if (error?.message?.includes("Export failed: 404")) {
        errorMessage = "Batch không tồn tại hoặc đã bị xóa.";
      } else if (error?.message) {
        errorMessage = `Lỗi xuất file: ${error.message}`;
      }

      showNotification(errorMessage, "error");
    } finally {
      setIsExporting(false);
    }
  };

  // Handle create code
  const handleCreateCode = async (formData: {
    batch: string;
    quantity: number;
  }) => {
    try {
      console.log("Creating codes:", formData);

      // Call actual API
      const requestData: CreateCodeRequest = {
        batch: formData.batch,
        num_code: formData.quantity,
      };

      const response = await apiService.createCode(requestData);
      console.log("Create code response:", response);
      console.log("Response structure:", {
        success: response?.success,
        message: response?.message,
        data: response?.data,
        codes_created: response?.codes_created,
        batch: response?.batch,
        codes: response?.codes,
      });

      if (response && response.success) {
        setCreateModal({ isOpen: false });

        // Safely access the response data - handle both possible structures
        const codesCreated =
          response.data?.codes_created ||
          response.codes_created ||
          formData.quantity;

        showNotification(`Đã tạo thành công ${codesCreated} mã code`);
        // Refresh data to show newly created codes
        fetchData();
      } else {
        throw new Error(response?.message || "Không thể tạo mã code");
      }
    } catch (error: any) {
      console.error("Error creating codes:", error);
      console.error("Error details:", {
        message: error.message,
        errors: error.errors,
        stack: error.stack,
      });

      // Show the actual error message from API
      const errorMessage = error.message || "Lỗi khi tạo mã code";
      showNotification(errorMessage, "error");
    }
  };

  // Handle delete codes
  const handleDeleteCodes = async (numCode: number) => {
    try {
      setIsDeleting(true);
      console.log("Deleting codes:", numCode);

      const response = await apiService.deleteCodes(numCode);
      console.log("Delete codes response:", response);

      if (response.success) {
        setDeleteModal({ isOpen: false });
        showNotification(
          `Đã xóa thành công ${response.deleted_count} mã code chưa sử dụng`
        );
        // Refresh data to show updated codes
        fetchData();
      } else {
        throw new Error(response.message || "Không thể xóa mã code");
      }
    } catch (error: any) {
      console.error("Error deleting codes:", error);
      showNotification(
        error?.message || "Lỗi khi xóa mã code. Vui lòng thử lại.",
        "error"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle delete single code
  const handleDeleteCode = async (codeId: string) => {
    try {
      setIsDeletingCode(true);
      console.log("Deleting code:", codeId);

      const response = await apiService.deleteCode(codeId);
      console.log("Delete code response:", response);

      if (response.success) {
        setDeleteCodeModal({ isOpen: false, codeId: "", code: "" });
        showNotification("Đã xóa mã code thành công");
        // Refresh data to show updated codes
        fetchData();
      } else {
        throw new Error(response.message || "Không thể xóa mã code");
      }
    } catch (error: any) {
      console.error("Error deleting code:", error);
      showNotification(
        error?.message || "Lỗi khi xóa mã code. Vui lòng thử lại.",
        "error"
      );
    } finally {
      setIsDeletingCode(false);
    }
  };

  // Handle open delete code modal
  const handleOpenDeleteCodeModal = (codeId: string, code: string) => {
    setDeleteCodeModal({
      isOpen: true,
      codeId,
      code,
    });
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  // Fetch campaigns for filter dropdown
  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await apiService.getCampaigns();
      if (response.success) {
        const campaignOptions = response.data.map((campaign) => ({
          id: campaign._id || "",
          name: campaign.name,
        }));
        setCampaigns(campaignOptions);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  }, []);

  // Fetch batches for filter dropdown
  const fetchBatches = useCallback(async () => {
    try {
      const response = await apiService.getBatches();
      if (response.success) {
        const batchOptions = response.data.map((batch) => ({
          id: batch._id || "",
          name: batch.name,
        }));
        setBatches(batchOptions);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
  }, []);

  // Fetch campaigns when component mounts
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Fetch batches when component mounts
  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter(isAdmin ? "all" : "used"); // User không thể reset về "all"
    setCampaignFilter("all");
    setBatchFilter("all");
    setUsedDateStart("");
    setUsedDateEnd("");
    setCreatedDateStart("");
    setCreatedDateEnd("");
    setCurrentPage(1);
    // Data will be refetched automatically due to useEffect dependencies
  };

  return (
    <div className="">
      {/* Header */}
      <CodeHeader
        onCreateCode={
          isAdmin ? () => setCreateModal({ isOpen: true }) : undefined
        }
        onExportCodes={isAdmin ? handleExportCodes : undefined}
        onDeleteCodes={
          isAdmin ? () => setDeleteModal({ isOpen: true }) : undefined
        }
      />

      {/* Filters */}
      <CodeFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={isAdmin ? setStatusFilter : undefined}
        campaignFilter={campaignFilter}
        onCampaignFilterChange={setCampaignFilter}
        campaigns={campaigns}
        batchFilter={batchFilter}
        onBatchFilterChange={setBatchFilter}
        batches={batches}
        stats={{
          total: totalCode,
          used: totalCodeUsed,
          notUsed: totalCodeNotUsed,
        }}
        usedDateStart={usedDateStart}
        onUsedDateStartChange={setUsedDateStart}
        usedDateEnd={usedDateEnd}
        onUsedDateEndChange={setUsedDateEnd}
        createdDateStart={createdDateStart}
        onCreatedDateStartChange={setCreatedDateStart}
        createdDateEnd={createdDateEnd}
        onCreatedDateEndChange={setCreatedDateEnd}
        onClearFilters={handleClearFilters}
        hideUnusedOption={!isAdmin} // Ẩn option "Chưa sử dụng" cho role User
        hideStatusFilter={!isAdmin} // Ẩn hoàn toàn dropdown trạng thái cho role User
        isAdmin={isAdmin} // Truyền role Admin
      />

      {/* Table with Pagination */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 w-24 xl:w-32">
                  Batch
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 w-24 xl:w-48">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 w-32 xl:w-40">
                  Tình trạng
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 xl:w-48">
                  Chiến dịch
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 w-36 xl:w-44">
                  Ngày sử dụng
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 w-36 xl:w-44">
                  Ngày tạo
                </th>
                {isAdmin && (
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 w-20">
                    Thao tác
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                      <p className="text-gray-600 dark:text-gray-300">Đang tải dữ liệu...</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedCodes.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800"
                  >
                    <div className="flex flex-col items-center">
                      <svg
                        className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2">
                        {campaignFilter !== "all"
                          ? "Không tìm thấy mã code nào cho chiến dịch này"
                          : "Không có dữ liệu code nào"}
                      </p>
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        {campaignFilter !== "all"
                          ? "Chiến dịch được chọn không có mã code nào"
                          : "Chưa có mã code nào được tạo"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedCodes.map((code) => (
                  <tr
                    key={code.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-4 py-4 whitespace-nowrap w-24 xl:w-32">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {code.batch}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap w-24 xl:w-48">
                      <div className="text-sm font-mono font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-3 py-0.5 rounded inline-block">
                        {code.code}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap w-32 xl:w-40">
                      {code.status === "used" ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Đã sử dụng
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L9 9.586l2.293-2.293a1 1 0 011.414 1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Chưa sử dụng
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap xl:w-48">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {code.campaign || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap w-36 xl:w-44">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {code.usedDate
                          ? new Date(code.usedDate).toLocaleDateString(
                              "vi-VN",
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                          : "-"}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap w-36 xl:w-44">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {new Date(code.createdAt).toLocaleDateString("vi-VN", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-4 whitespace-nowrap text-center w-20">
                        <button
                          onClick={() =>
                            code.status === "unused" &&
                            handleOpenDeleteCodeModal(code.id, code.code)
                          }
                          disabled={code.status === "used"}
                          className={`p-2 rounded-md transition-colors ${
                            code.status === "used"
                              ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                              : "text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                          }`}
                          title={
                            code.status === "used"
                              ? "Không thể xóa mã code đã sử dụng"
                              : "Xóa mã code"
                          }
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalItems > 0 && (
          <CodePagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        )}
      </div>

      {/* Create Code Modal - Only for Admin */}
      {isAdmin && (
        <CreateCodeModal
          isOpen={createModal.isOpen}
          onClose={() => setCreateModal({ isOpen: false })}
          onSubmit={handleCreateCode}
        />
      )}

      {/* Delete Codes Modal - Only for Admin */}
      {isAdmin && (
        <DeleteCodesModal
          isOpen={deleteModal.isOpen}
          onClose={() => setDeleteModal({ isOpen: false })}
          onSubmit={handleDeleteCodes}
          isLoading={isDeleting}
          maxCode={totalCodeNotUsed}
        />
      )}

      {/* Confirm Delete Single Code Modal - Only for Admin */}
      {isAdmin && (
        <ConfirmDeleteCodeModal
          isOpen={deleteCodeModal.isOpen}
          onClose={() =>
            setDeleteCodeModal({ isOpen: false, codeId: "", code: "" })
          }
          onConfirm={() => handleDeleteCode(deleteCodeModal.codeId)}
          code={deleteCodeModal.code}
          isLoading={isDeletingCode}
        />
      )}

      {/* Export Code Modal */}
      <ExportCodeModal
        isOpen={exportModal.isOpen}
        onClose={() => setExportModal({ isOpen: false })}
        onExport={handleExportCodesWithParams}
        isLoading={isExporting}
      />

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right duration-300">
          <div
            className={`px-4 py-3 rounded-md shadow-lg flex items-center ${
              toastType === "success"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            {toastMessage}
            <button
              onClick={() => setShowToast(false)}
              className="ml-2 text-white/80 hover:text-white"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeManagement;
